/**
 * MONSTER TOWN — BUG CATCHING. FISHING/BUGS CREW (src/catching/**).
 *
 * Expansion brief §6, in full: "creatures wander the grass and trees; walk up
 * and tap to net them." That is the entire interaction. There is no net to aim,
 * no timing, no stealth meter, no bug that "gets away" — a three-year-old taps
 * a butterfly and the butterfly is theirs.
 *
 * WHAT IS OUT IS THE GAME. Which creature spawns is never chosen here: the
 * spawner asks `rollAvailable('bug', engine.rng, place)` and the answer comes
 * from the availability rules in `src/content/creatures.ts`. Tap at ten in the
 * morning and you get butterflies; come back at nine at night and the same
 * patch of grass has fireflies in it. No species name appears in this file.
 *
 * LAW #2, THE THREE WAYS IT COULD HAVE BEEN BROKEN AND WAS NOT:
 *  - a bug never flees a nearby monster (it wanders on its own little leash);
 *  - a bug that times out is not lost, because another one takes its place and
 *    the book does not care WHICH firefly you got;
 *  - a tap that lands near a bug is generous by 70 screen pixels, and a tap
 *    that misses just walks you there, which is what tapping the ground does
 *    anyway.
 *
 * PERF — this is a per-frame system on the awake scene, which the README warns
 * about by name. It is kept honest by: a hard cap of four bugs; no work at all
 * when the town is not the awake scene, when the mode is not play, or when a
 * transition is in flight; trig-only animation with zero allocation per frame;
 * and shared cached geometry/materials from `src/render/kit.ts`.
 */

import * as THREE from 'three'
import {
  collection,
  getCollectible,
  rollAvailable,
  type CollectionEntry,
} from '../core/collection'
import { engine } from '../core/engine'
import { bus } from '../core/events'
import { world } from '../core/scene'
import type { SceneId, Vec3 } from '../core/types'
import { app, type GameContext } from '../app/context'
import { instanceCentre } from '../app/interactions'
import { worldToCanvasPx } from '../input/pick'
import { animateCreature, creatureMesh, creatureParts, type CreatureParts } from './art'
import { placeTagAt, spawnSpot, type PlaceTag } from './places'
import { claimCatch } from './reward'
import { goThen } from './walk'

const TOWN = 'town' as SceneId

/** Hard population cap. Four is plenty on screen and cheap on the old iPad. */
const MAX_BUGS = 4
/** Seconds between spawn attempts. */
const SPAWN_EVERY = 2.6
/** A bug wanders off after this long, so the set turns over with the clock. */
const LIFE_S = 75
/** Further than this from the monster and it is not worth simulating. */
const FORGET_RADIUS = 30
/** Fat-finger slop, in CANVAS pixels. Never window pixels — the hard rule. */
const TAP_SLOP_PX = 70
/** Where the monster stands to net one. */
const NET_REACH = 1.5

interface Bug {
  entry: CollectionEntry
  art: THREE.Group
  /** Looked up once. See the note on `creatureParts`. */
  parts: CreatureParts
  home: Vec3
  at: Vec3
  place: PlaceTag
  /** Seconds of life, drives the flap and the wander. */
  t: number
  /** Phase offset so four bugs never move in lockstep. */
  seed: number
  taken: boolean
}

const bugs: Bug[] = []
let ctxRef: GameContext | null = null
let spawnIn = 1.2
let installed = false
/**
 * NOTHING COMPETES WITH THE FIRST RUN. The tutorial's second beat is "tap the
 * nearest thing you can see", and it waits on the kid actually using that
 * thing — a butterfly drifting within this crew's 70 px slop would eat the tap
 * and the paw would sit there for ever. It is also simply the wrong moment: a
 * three-year-old meeting the game for the first time does not need a second
 * thing on screen asking to be tapped. The bugs come out when the paw leaves.
 *
 * IT IS A DEADLINE, NOT A FLAG, and that is deliberate. A sticky boolean can
 * only be cleared by `tutorial:finished`, and there is at least one path that
 * puts the paw away without emitting it (`__tut.stop()`), which would leave a
 * kid's town with no creatures in it for the rest of the session. The tutorial
 * re-pulses roughly every eight idle seconds while a beat is up, so
 * `tutorial:pulse` is a heartbeat: the hold renews while the paw is really
 * there and lapses on its own if it is not. The worst case is that bugs come
 * back mid-tutorial, which is exactly where this crew started.
 *
 * ===========================================================================
 *  `NO_HOLD` IS -Infinity AND NOT 0, BECAUSE `engine.now` CAN BE NEGATIVE.
 * ===========================================================================
 * `Engine.now` starts at 0 and accumulates `rafTimestamp - performance.now()`
 * sampled at start — and a rAF timestamp is the moment the FRAME began, which
 * is routinely a few tens of milliseconds EARLIER than the `performance.now()`
 * read taken later in the same turn. So the first frame contributes a small
 * negative number and `engine.now` sits at roughly -70 ms until enough frames
 * have run to climb back through zero.
 *
 * With 0 as the sentinel, `engine.now < tutorialUntil` was therefore TRUE at
 * boot with no tutorial anywhere near the screen, and this crew stood its
 * spawner down — a kid's town with no creatures in it, for no reason, exactly
 * the failure the "deadline, not a flag" note above was written to avoid.
 * Measured: `now=-73 until=0`, with `navigator.webdriver` true so the first run
 * had provably never started. It made `catch-check`'s "tapping it walks the
 * monster over and nets it" fail intermittently, and the diagnosis string now
 * prints both numbers so the next person does not have to find this twice.
 *
 * A sentinel compared against a clock has to be unreachable BY THAT CLOCK.
 */
const NO_HOLD = Number.NEGATIVE_INFINITY
let tutorialUntil = NO_HOLD
const TUTORIAL_HOLD_MS = 20_000
/** A catch in flight — the bug is reserved until the monster gets there. */
let reserved: Bug | null = null
/**
 * WHY THE LAST TAP WAS OR WAS NOT A CATCH. Read only by `catch-check`, and it
 * exists because "the tap landed on the canvas and nothing happened" is the one
 * failure this crew cannot diagnose from the outside: every step of the funnel
 * is a silent early return. Costs one string assignment per tap.
 */
let lastTapWhy = 'none'

export function tapDiagnosis(): string {
  return lastTapWhy
}

function townNode(): ReturnType<typeof world.get> {
  return world.get(TOWN)
}

function awake(): boolean {
  const node = townNode()
  if (node === undefined || !node.awake) return false
  if (world.activeSceneId !== TOWN) return false
  if (world.transitioning) return false
  if (engine.now < tutorialUntil) return false
  return true
}

// ---------------------------------------------------------------------------
// spawning
// ---------------------------------------------------------------------------

/**
 * Which PLACE to try next. Rotates so a kid standing in the middle of the town
 * sees grass bugs, garden bugs and tree bugs rather than four of one — the
 * categorisation only teaches anything if the categories are both on screen.
 */
const PLACE_CYCLE: readonly PlaceTag[] = ['grass', 'tree', 'garden', 'grass']
let placeTurn = 0

function trySpawn(): void {
  if (bugs.length >= MAX_BUGS) return
  const node = townNode()
  if (node === undefined) return
  const want = PLACE_CYCLE[placeTurn % PLACE_CYCLE.length] ?? 'grass'
  placeTurn++

  const spot = spawnSpot(app.playerPos, want, engine.rng)
  if (spot === null) return
  // The spot decides the place, not the wish: a 'tree' wish that landed on open
  // grass must roll a GRASS bug, or a beetle turns up in the middle of a lawn.
  let entry = rollAvailable('bug', engine.rng, spot.place)
  if (entry === null) {
    // Nothing is out at this tag right now. Widen rather than tell a kid "no"
    // (collection.ts, rollAvailable's own contract note).
    entry = rollAvailable('bug', engine.rng, 'grass')
  }
  if (entry === null) return
  // Never two of the same on screen at once — a page fills faster and the
  // screen reads as a variety of animals rather than a swarm.
  if (bugs.some((b) => b.entry.id === entry.id)) return

  const high = entry.tags?.includes('flyer') === true
  const y = high ? 1.15 : 0.32
  // The plate under a crawler has to lie on the grass, so the art needs to know
  // how high above it the creature is about to be hung.
  const art = creatureMesh(entry.id, y)
  art.position.set(spot.at.x, y, spot.at.z)
  node.actors.add(art)
  bugs.push({
    entry,
    art,
    parts: creatureParts(art),
    home: { x: spot.at.x, y, z: spot.at.z },
    at: { x: spot.at.x, y, z: spot.at.z },
    place: spot.place,
    t: 0,
    seed: engine.rng() * 100,
    taken: false,
  })
}

function remove(b: Bug, poof: boolean): void {
  const i = bugs.indexOf(b)
  if (i >= 0) bugs.splice(i, 1)
  if (reserved === b) reserved = null
  b.art.removeFromParent()
  if (poof) {
    bus.emit('fx:play', { effect: 'sparkle', at: { ...b.at }, count: 4 })
  }
}

/** Everything goes away — a slot switch, a cleared town, leaving the scene. */
export function clearBugs(): void {
  for (const b of [...bugs]) remove(b, false)
  bugs.length = 0
  reserved = null
}

// ---------------------------------------------------------------------------
// catching
// ---------------------------------------------------------------------------

function net(b: Bug): void {
  if (b.taken) return
  b.taken = true
  const at: Vec3 = { x: b.at.x, y: 0.4, z: b.at.z }
  claimCatch(b.entry, at)
  remove(b, true)
  // The screen is one short, so top it back up promptly rather than in 2.6 s.
  spawnIn = Math.min(spawnIn, 0.6)
}

/** Where the monster stands to reach a bug. */
function standFor(b: Bug): Vec3 {
  const p = app.playerPos
  const dx = p.x - b.at.x
  const dz = p.z - b.at.z
  const d = Math.hypot(dx, dz) || 1
  return { x: b.at.x + (dx / d) * NET_REACH, y: 0, z: b.at.z + (dz / d) * NET_REACH }
}

/**
 * The nearest bug to a tap, MEASURED IN CANVAS PIXELS. Same trick, and the same
 * reason, as `sceneryAt` in the world crew's hotspots: a bug hovering at head
 * height puts its ground ray several metres behind itself, so a ground-distance
 * test misses every flyer while hitting every crawler.
 */
/** Diagnostic only: how far the nearest bug was, in canvas px. */
function nearestBugPx(point: { x: number; y: number }): number {
  if (ctxRef === null) return Infinity
  let best = Infinity
  for (const b of bugs) {
    const px = worldToCanvasPx(ctxRef.camera.camera, ctxRef.canvas, b.at)
    if (px.behind) continue
    best = Math.min(best, Math.hypot(point.x - px.x, point.y - px.y))
  }
  return best
}

function bugAt(point: { x: number; y: number }): Bug | null {
  if (ctxRef === null) return null
  let best: Bug | null = null
  let bestD = Infinity
  for (const b of bugs) {
    if (b.taken) continue
    const px = worldToCanvasPx(ctxRef.camera.camera, ctxRef.canvas, b.at)
    if (px.behind) continue
    const d = Math.hypot(point.x - px.x, point.y - px.y)
    if (d < TAP_SLOP_PX && d < bestD) {
      bestD = d
      best = b
    }
  }
  return best
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installBugs(ctx: GameContext): void {
  if (installed) return
  installed = true
  ctxRef = ctx

  bus.on('input:tap', (e) => {
    if (e.mode !== 'play') {
      lastTapWhy = `mode:${e.mode}`
      return
    }
    if (!awake()) {
      lastTapWhy = `asleep scene=${String(world.activeSceneId)} moving=${world.transitioning} tut=${engine.now < tutorialUntil} now=${Math.round(engine.now)} until=${Math.round(tutorialUntil)}`
      return
    }
    const b = bugAt(e.point)
    if (b === null) {
      lastTapWhy = `noBugNear bugs=${bugs.length} nearest=${Math.round(nearestBugPx(e.point))}px`
      return
    }
    if (b.taken) {
      lastTapWhy = 'alreadyTaken'
      return
    }
    /**
     * WHEN A BUG IS SITTING OVER A PLACED THING, THE NEARER ONE WINS.
     *
     * The first version made a placed thing win OUTRIGHT, to protect somebody
     * else's gate: `playthrough`'s LAW #1 sweep taps props, and a butterfly
     * drifting across a bench must never make that bench score as dead. But
     * outright is too strong, and `catch-check` caught it — intermittently, in
     * two runs out of six, because where a bug spawns is random. The reported
     * symptom was "the tap landed on the canvas, nothing happened, and no walk
     * was ever started", which under LAW #1 is a creature a kid cannot catch.
     * `pickInstance` is deliberately tolerant by 44 px, so any bug within about
     * a finger of the kid's own crate was silently unreachable.
     *
     * Nearness settles it instead, with the tie going to the prop: the sweep
     * taps a prop's own centre (distance ~0, so the prop always wins) and a kid
     * aiming at a butterfly hovering beside a bench gets the butterfly.
     */
    if (e.instanceId !== null && ctxRef !== null) {
      const c = instanceCentre(e.sceneId, e.instanceId)
      if (c !== null) {
        const propPx = worldToCanvasPx(ctxRef.camera.camera, ctxRef.canvas, c)
        const bugPx = worldToCanvasPx(ctxRef.camera.camera, ctxRef.canvas, b.at)
        const propD = propPx.behind
          ? Infinity
          : Math.hypot(e.point.x - propPx.x, e.point.y - propPx.y)
        const bugD = bugPx.behind ? Infinity : Math.hypot(e.point.x - bugPx.x, e.point.y - bugPx.y)
        if (propD <= bugD) {
          lastTapWhy = `prop wins ${Math.round(propD)}px vs bug ${Math.round(bugD)}px`
          return
        }
      }
    }
    lastTapWhy = `catching ${b.entry.id}`
    reserved = b
    // Point at it on the way, so the kid can see which one they picked.
    bus.emit('audio:cue', { cue: 'ui_tap' })
    goThen(standFor(b), () => {
      // It may have wandered off, or the kid may have tapped another one on the
      // way. Either way something still happens: catch whatever is nearest.
      if (!b.taken && bugs.includes(b)) net(b)
      else {
        const near = bugs.find(
          (x) => !x.taken && Math.hypot(x.at.x - app.playerPos.x, x.at.z - app.playerPos.z) < 3.5,
        )
        if (near !== undefined) net(near)
      }
    })
  })

  bus.on('scene:left', (e) => {
    if (e.scene === TOWN) clearBugs()
  })
  bus.on('save:loaded', () => clearBugs())
  /**
   * THE HEARTBEAT MAY NOT OUTLIVE THE CLAIM.
   *
   * The deadline above is the right shape and it was not enough on its own: the
   * paw re-pulses about every eight idle seconds FOR EVER, so for a kid who
   * stalls on a beat the heartbeat renewed the hold for ever too and their town
   * had no creatures in it for the rest of the session. `tutorial:claim` is the
   * bounded answer to "is the first run actually being followed" (see the note
   * in `src/ui/tutorial.ts`); a pulse may renew the hold but it may not revive
   * a claim that has lapsed.
   */
  let tutorialClaim = false
  const holdForTutorial = (): void => {
    if (!tutorialClaim) return
    tutorialUntil = engine.now + TUTORIAL_HOLD_MS
  }
  bus.on('tutorial:claim', (e) => {
    tutorialClaim = e.claimed
    if (e.claimed) {
      tutorialUntil = engine.now + TUTORIAL_HOLD_MS
      clearBugs()
    } else {
      tutorialUntil = NO_HOLD
      spawnIn = 2
    }
  })
  bus.on('tutorial:started', () => {
    clearBugs()
  })
  // The heartbeat: the paw pulses while it waits, so the hold renews itself.
  bus.on('tutorial:beat', holdForTutorial)
  bus.on('tutorial:pulse', holdForTutorial)
  bus.on('tutorial:finished', () => {
    tutorialUntil = NO_HOLD
    spawnIn = 2
  })

  engine.addSystem({
    id: 'catching.bugs',
    order: 48,
    fixed: (dt) => {
      if (!awake() || app.mode !== 'play') return
      spawnIn -= dt
      if (spawnIn <= 0) {
        spawnIn = SPAWN_EVERY
        trySpawn()
      }
      const p = app.playerPos
      for (let i = bugs.length - 1; i >= 0; i--) {
        const b = bugs[i]
        if (b === undefined) continue
        b.t += dt
        // A gentle leash: it pootles round its own patch and never runs away.
        const ang = b.t * 0.55 + b.seed
        const r = b.place === 'tree' ? 0.7 : 1.15
        b.at.x = b.home.x + Math.cos(ang) * r
        b.at.z = b.home.z + Math.sin(ang * 0.8) * r
        b.at.y = b.home.y
        b.art.position.set(b.at.x, b.at.y, b.at.z)
        b.art.rotation.y = -ang
        if (b === reserved) continue
        const far = Math.hypot(b.at.x - p.x, b.at.z - p.z) > FORGET_RADIUS
        if (b.t > LIFE_S || far) remove(b, false)
      }
    },
    render: () => {
      if (!awake()) return
      for (const b of bugs) animateCreature(b.parts, b.entry.id, b.t)
    },
  })
}

// ---------------------------------------------------------------------------
// test surface (read by tools/catch-check.mjs — never by the game)
// ---------------------------------------------------------------------------

export interface BugReport {
  id: string
  place: string
  at: Vec3
  found: boolean
  onScreen: boolean
}

export function bugReport(): BugReport[] {
  return bugs.map((b) => {
    const px =
      ctxRef === null ? null : worldToCanvasPx(ctxRef.camera.camera, ctxRef.canvas, b.at)
    const rect = ctxRef?.canvas.getBoundingClientRect() ?? null
    const onScreen =
      px !== null &&
      rect !== null &&
      !px.behind &&
      px.x >= 0 &&
      px.y >= 0 &&
      px.x <= rect.width &&
      px.y <= rect.height
    return {
      id: b.entry.id,
      place: b.place,
      at: { ...b.at },
      found: collection.has(b.entry.id),
      onScreen,
    }
  })
}

/** Force one out next to the monster. QA only; the game never calls this. */
export function forceBug(id?: string): string | null {
  const node = townNode()
  if (node === undefined) return null
  const entry =
    id === undefined
      ? rollAvailable('bug', engine.rng, 'grass')
      : (getCollectible(id) ?? null)
  if (entry === null || entry.kind !== 'bug') return null
  const spot = spawnSpot(app.playerPos, placeTagAt(app.playerPos.x, app.playerPos.z), engine.rng)
  const at = spot?.at ?? { x: app.playerPos.x + 2, y: 0, z: app.playerPos.z }
  const y = entry.tags?.includes('flyer') === true ? 1.15 : 0.32
  const art = creatureMesh(entry.id, y)
  art.position.set(at.x, y, at.z)
  node.actors.add(art)
  bugs.push({
    entry,
    art,
    parts: creatureParts(art),
    home: { x: at.x, y, z: at.z },
    at: { x: at.x, y, z: at.z },
    place: placeTagAt(at.x, at.z),
    t: 0,
    seed: engine.rng() * 100,
    taken: false,
  })
  return entry.id
}

/** Net the nearest bug without a tap. QA only. */
export function forceNet(): string | null {
  const b = bugs.find((x) => !x.taken)
  if (b === undefined) return null
  const id = b.entry.id
  net(b)
  return id
}
